import { useCallback, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Status as WfStatus, Tooltip } from '@wf/components';

import { Status } from '../../modules/License/types/enums';

import useResizeObserver from '../../hooks/useResizeObserver';

import styles from './LicenseStatus.module.scss';

type ColorType = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'caution' | 'typography' | 'indigo';

const tagColors: Record<Status, ColorType> = {
  [Status.STORED]: 'primary',
  [Status.DELETED]: 'error',
  [Status.QUERIED]: 'error',
  [Status.PENDING_PAYMENT]: 'info',
  [Status.REQUESTED]: 'primary',
  [Status.PARTIALLY_APPROVED]: 'indigo',
  [Status.APPROVED]: 'success',
  [Status.PARTIALLY_USED]: 'success',
  [Status.USED]: 'success',
  [Status.SUSPENDED]: 'warning',
  [Status.EXPIRED]: 'warning',
  [Status.REJECTED]: 'error',
  [Status.CANCELED]: 'error',
  [Status.GENERATED]: 'primary',
  [Status.AMENDMENT_REQUESTED]: 'info',
};

interface ILicenseStatusProps {
  status: string;
  ogaStatus: string | null;
  ogaStatusInNationalLang: string | null;
  size?: 'sm' | 'md';
}

function LicenseStatus({ status, ogaStatus, ogaStatusInNationalLang, size = 'md' }: ILicenseStatusProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const ref = useRef<HTMLDivElement>(null);

  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  const observerCallback = useCallback(
    () => setIsEllipsisActive(!!ref.current && ref.current.offsetWidth < ref.current.scrollWidth),
    []
  );

  useResizeObserver(ref, observerCallback);

  const statusText =
    status in Status ? t(status) : language === 'en' ? ogaStatus : ogaStatusInNationalLang ?? ogaStatus;

  if (!statusText) {
    return null;
  }

  return (
    <Tooltip triggers={isEllipsisActive ? ['hover'] : []} placement="right">
      <Tooltip.Target className={styles.tooltipTarget}>
        <WfStatus color={tagColors[status as Status] || 'indigo'} className={styles.status} size={size} ref={ref}>
          {statusText}
        </WfStatus>
      </Tooltip.Target>
      <Tooltip.Content>{statusText}</Tooltip.Content>
    </Tooltip>
  );
}

export default LicenseStatus;
